import { onBeforeUnmount, onMounted, ref, toValue, watch } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import ScheduledWorkflowsAPI from 'custom/api/scheduledWorkflows';

const TIMELINE_UPDATED_EVENT = 'contacta:scheduled-workflows:updated';
const REFRESH_INTERVAL = 30000;

export const notifyScheduledWorkflowTimeline = conversationId => {
  emitter.emit(TIMELINE_UPDATED_EVENT, conversationId);
};

export const useScheduledWorkflowTimeline = conversationId => {
  const enabled = ref(false);
  const workflows = ref([]);
  const isLoading = ref(false);
  let requestSequence = 0;
  let refreshTimer;

  const loadWorkflows = async ({ showLoading = false } = {}) => {
    const currentConversationId = toValue(conversationId);
    requestSequence += 1;
    const currentRequest = requestSequence;

    if (!currentConversationId) {
      enabled.value = false;
      workflows.value = [];
      return;
    }

    if (showLoading) isLoading.value = true;

    try {
      const { data } = await ScheduledWorkflowsAPI.listPending(
        currentConversationId
      );
      if (
        currentRequest !== requestSequence ||
        currentConversationId !== toValue(conversationId)
      ) {
        return;
      }

      enabled.value = data.enabled;
      workflows.value = data.workflows || [];
    } catch {
      if (currentRequest === requestSequence) {
        enabled.value = false;
        workflows.value = [];
      }
    } finally {
      if (currentRequest === requestSequence) isLoading.value = false;
    }
  };

  const handleTimelineUpdate = updatedConversationId => {
    if (Number(updatedConversationId) === Number(toValue(conversationId))) {
      loadWorkflows();
    }
  };

  const handleWindowFocus = () => loadWorkflows();

  watch(
    () => toValue(conversationId),
    () => {
      enabled.value = false;
      workflows.value = [];
      loadWorkflows({ showLoading: true });
    },
    { immediate: true }
  );

  onMounted(() => {
    emitter.on(TIMELINE_UPDATED_EVENT, handleTimelineUpdate);
    window.addEventListener('focus', handleWindowFocus);
    refreshTimer = window.setInterval(() => {
      if (!document.hidden) loadWorkflows();
    }, REFRESH_INTERVAL);
  });

  onBeforeUnmount(() => {
    requestSequence += 1;
    emitter.off(TIMELINE_UPDATED_EVENT, handleTimelineUpdate);
    window.removeEventListener('focus', handleWindowFocus);
    window.clearInterval(refreshTimer);
  });

  return {
    enabled,
    workflows,
    isLoading,
    loadWorkflows,
  };
};
